import { useEffect, useState } from 'react';

const MUSIC_URL = 'https://music.kiwihouse.earth';
const SESSION_KEY = 'quietlodge-unlocked';
const DOOR_PARAM = 'door';
const ARPEGGI_URL = 'https://apps.apple.com/app/arpeggi/id6503619183';
const AMPERFY_URL = 'https://apps.apple.com/app/amperfy-music/id1530145038';

function stripDoorFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(DOOR_PARAM)) return;
  url.searchParams.delete(DOOR_PARAM);
  const clean = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', clean);
}

function RoomShell({ children }) {
  return (
    <main className="ql-room min-h-screen font-montserrat">
      <div className="ql-grain" aria-hidden="true" />
      <div className="ql-scan" aria-hidden="true" />
      <div className="ql-vignette" aria-hidden="true" />
      <div className="ql-content">{children}</div>
    </main>
  );
}

function QuietLodge() {
  const [unlocked, setUnlocked] = useState(false);
  const [doorPassword, setDoorPassword] = useState('');
  const [doorError, setDoorError] = useState('');
  const [checkingLink, setCheckingLink] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [createdUsername, setCreatedUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const tryUnlock = async (candidate) => {
    const res = await fetch('/.netlify/functions/create-music-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'checkDoor', doorPassword: candidate }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) {
      const err = new Error(data.error || 'Wrong door password.');
      err.status = res.status;
      throw err;
    }
    sessionStorage.setItem(SESSION_KEY, '1');
    sessionStorage.setItem(`${SESSION_KEY}-pw`, candidate);
    setDoorPassword(candidate);
    setUnlocked(true);
  };

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    const prevTitle = document.title;
    document.title = 'Quiet Lodge';
    return () => {
      document.head.removeChild(robots);
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const params = new URLSearchParams(window.location.search);
      const fromLink = params.get(DOOR_PARAM);

      if (fromLink) {
        try {
          await tryUnlock(fromLink);
          stripDoorFromUrl();
        } catch (err) {
          if (!cancelled) {
            setDoorError(err.message || 'This invite link is invalid.');
            stripDoorFromUrl();
          }
        } finally {
          if (!cancelled) setCheckingLink(false);
        }
        return;
      }

      if (sessionStorage.getItem(SESSION_KEY) === '1') {
        const stored = sessionStorage.getItem(`${SESSION_KEY}-pw`) || '';
        if (!cancelled) {
          setDoorPassword(stored);
          setUnlocked(true);
        }
      }
      if (!cancelled) setCheckingLink(false);
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, []);

  const unlock = async (event) => {
    event.preventDefault();
    setDoorError('');
    if (!doorPassword.trim()) {
      setDoorError('Enter the door password.');
      return;
    }
    try {
      await tryUnlock(doorPassword);
    } catch (err) {
      setDoorError(err.message || 'Wrong door password.');
    }
  };

  const copyServerUrl = async () => {
    try {
      await navigator.clipboard.writeText(MUSIC_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const createAccount = async (event) => {
    event.preventDefault();
    setFormStatus({ type: '', message: '' });

    if (password !== confirmPassword) {
      setFormStatus({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    if (username.trim().length < 2) {
      setFormStatus({ type: 'error', message: 'Username needs at least 2 characters.' });
      return;
    }
    if (password.length < 8) {
      setFormStatus({ type: 'error', message: 'Password needs at least 8 characters.' });
      return;
    }

    const storedDoor =
      doorPassword || sessionStorage.getItem(`${SESSION_KEY}-pw`) || '';

    setSubmitting(true);
    try {
      const res = await fetch('/.netlify/functions/create-music-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doorPassword: storedDoor,
          username: username.trim(),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setFormStatus({
          type: 'error',
          message: data.error || 'Could not create account.',
        });
        return;
      }
      setCreatedUsername(username.trim());
      setFormStatus({
        type: 'success',
        message: 'Signal locked. Use that username & password in an app below.',
      });
      setPassword('');
      setConfirmPassword('');
    } catch {
      setFormStatus({
        type: 'error',
        message: 'Network error creating account. Try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingLink) {
    return (
      <RoomShell>
        <div className="min-h-screen flex items-center justify-center px-[5vw]">
          <p className="text-sm font-light tracking-[0.35em] uppercase text-[var(--ql-mute)] m-0">
            syncing…
          </p>
        </div>
      </RoomShell>
    );
  }

  if (!unlocked) {
    return (
      <RoomShell>
        <div className="min-h-screen flex items-center justify-center px-[5vw]">
          <form
            onSubmit={unlock}
            className="w-full max-w-sm flex flex-col items-center gap-8"
          >
            <div className="ql-title-box px-6 py-4 text-center w-full">
              <div className="text-[10px] uppercase tracking-[0.45em] text-[var(--ql-green)] mb-2">
                kiwi house
              </div>
              <div className="text-xl sm:text-2xl font-normal tracking-[0.12em] uppercase text-[var(--ql-ink)]">
                Quiet Lodge
              </div>
            </div>
            <input
              type="password"
              autoComplete="current-password"
              value={doorPassword}
              onChange={(e) => setDoorPassword(e.target.value)}
              className="ql-input text-center tracking-widest"
              placeholder="door password"
            />
            {doorError && (
              <p className="text-sm text-red-400/90 m-0 font-light" role="alert">
                {doorError}
              </p>
            )}
            <button
              type="submit"
              className="text-[11px] uppercase tracking-[0.4em] text-[var(--ql-green)] hover:text-[var(--ql-ink)]"
            >
              enter
            </button>
          </form>
        </div>
      </RoomShell>
    );
  }

  return (
    <RoomShell>
      <div className="min-h-screen px-[5vw] py-12 sm:py-16">
        <div className="mx-auto w-full max-w-md flex flex-col gap-12">
          <header className="flex flex-col items-center gap-4 text-center">
            <div className="ql-title-box px-6 py-4 w-full max-w-xs">
              <div className="text-[10px] uppercase tracking-[0.45em] text-[var(--ql-green)] mb-2">
                kiwi house
              </div>
              <div className="text-xl sm:text-2xl font-normal tracking-[0.12em] uppercase">
                Quiet Lodge
              </div>
            </div>
          </header>

          <section className="ql-panel p-5 sm:p-6 flex flex-col gap-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--ql-green)] m-0 mb-2">
                01 · account
              </p>
              <h2 className="text-base font-light tracking-wide m-0">
                Make a username & password
              </h2>
              <p className="text-sm font-light text-[var(--ql-mute)] m-0 mt-2">
                Same login for phone apps and the web player.
              </p>
            </div>
            <form onSubmit={createAccount} className="flex flex-col gap-4">
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="ql-input"
                placeholder="username"
                required
              />
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ql-input"
                placeholder="password (8+ characters)"
                required
              />
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="ql-input"
                placeholder="confirm password"
                required
              />
              {formStatus.message && (
                <p
                  className={`text-sm m-0 font-light ${
                    formStatus.type === 'error'
                      ? 'text-red-400/90'
                      : 'text-[var(--ql-green)]'
                  }`}
                  role="status"
                >
                  {formStatus.message}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="mt-1 self-start border border-[var(--ql-green)] text-[var(--ql-green)] py-2.5 px-6 text-[10px] uppercase tracking-[0.35em] hover:bg-[var(--ql-green)] hover:text-[var(--ql-void)] disabled:opacity-40 transition-colors"
              >
                {submitting ? 'Creating…' : 'Create account'}
              </button>
            </form>
          </section>

          <section className="flex flex-col gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--ql-green)] m-0 mb-2">
                02 · pick an app
              </p>
              <h2 className="text-base font-light tracking-wide m-0">
                Both are free. Try them both and see which one you like best.
              </h2>
            </div>

            <a
              href={ARPEGGI_URL}
              target="_blank"
              rel="noreferrer"
              className="ql-app block p-4 no-underline text-[var(--ql-ink)]"
            >
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <span className="text-sm tracking-wide uppercase">Arpeggi</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[var(--ql-green)]">
                  iOS 17+
                </span>
              </div>
              <p className="text-sm font-light text-[var(--ql-mute)] m-0">
                More stylized, Apple Music–like polish. Best for iOS&nbsp;17 or newer phones. Customizeable menus and layout.
              </p>
            </a>

            <a
              href={AMPERFY_URL}
              target="_blank"
              rel="noreferrer"
              className="ql-app block p-4 no-underline text-[var(--ql-ink)]"
            >
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <span className="text-sm tracking-wide uppercase">Amperfy</span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[var(--ql-mute)]">
                  older iOS ok
                </span>
              </div>
              <p className="text-sm font-light text-[var(--ql-mute)] m-0">
                Simpler and more flexible on older phones. I haven't used this one enough to comment yet.
              </p>
            </a>
          </section>

          <section className="ql-panel p-5 sm:p-6 flex flex-col gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--ql-green)] m-0 mb-2">
                03 · connect the app
              </p>
              <h2 className="text-base font-light tracking-wide m-0">
                {createdUsername
                  ? `Add the server, then log in as ${createdUsername}`
                  : 'After install, add the server and log in'}
              </h2>
            </div>

            <ol className="m-0 p-0 list-none flex flex-col gap-5">
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-[var(--ql-green)] text-[var(--ql-green)] text-xs tracking-wider">
                  A
                </span>
                <div className="flex flex-col gap-2 min-w-0">
                  <p className="text-sm font-light m-0 text-[var(--ql-ink)]">
                    Open the app and add a new server.
                  </p>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-[var(--ql-green)] text-[var(--ql-green)] text-xs tracking-wider">
                  B
                </span>
                <div className="flex flex-col gap-3 min-w-0">
                  <p className="text-sm font-light m-0 text-[var(--ql-ink)]">
                    Paste this as the server URL:
                  </p>
                  <div className="flex flex-wrap items-center gap-3 border border-white/10 px-3 py-3">
                    <code className="text-sm sm:text-base font-light text-[var(--ql-ink)] break-all">
                      {MUSIC_URL}
                    </code>
                    <button
                      type="button"
                      onClick={copyServerUrl}
                      className="text-[10px] uppercase tracking-[0.3em] text-[var(--ql-green)] hover:text-[var(--ql-ink)]"
                    >
                      {copied ? 'copied' : 'copy'}
                    </button>
                  </div>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-[var(--ql-green)] text-[var(--ql-green)] text-xs tracking-wider">
                  C
                </span>
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="text-sm font-light m-0 text-[var(--ql-ink)]">
                    Sign in with the username and password from step 1.
                  </p>
                  <p className="text-sm font-light text-[var(--ql-mute)] m-0">
                    That’s it — the library should appear after a moment.
                  </p>
                </div>
              </li>
            </ol>
          </section>

          <section className="border-t border-white/10 pt-8 flex flex-col gap-3 opacity-80">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--ql-mute)] m-0">
              04 · or the web (fine in a pinch)
            </p>
            <p className="text-sm font-light text-[var(--ql-mute)] m-0">
              Browser player works, but the apps above are nicer for everyday listening.
            </p>
            <a
              href={MUSIC_URL}
              target="_blank"
              rel="noreferrer"
              className="self-start text-[11px] uppercase tracking-[0.3em] text-[var(--ql-ink)] border-b border-[var(--ql-green)]/50 pb-0.5 no-underline hover:text-[var(--ql-green)]"
            >
              music.kiwihouse.earth
            </a>
          </section>
        </div>
      </div>
    </RoomShell>
  );
}

export default QuietLodge;
